import React, { createContext, useContext, useState } from 'react';

import type { Mode } from './types';

type ThemeMode = Extract<Mode, 'light' | 'dark'>;

interface ThemeModeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

interface ThemeModeProviderProps {
  children: React.ReactNode;
  initialMode?: ThemeMode;
}

export function ThemeModeProvider({ children, initialMode = 'light' }: ThemeModeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(initialMode);

  const value: ThemeModeContextValue = {
    mode,
    isDark: mode === 'dark',
    setMode,
    toggleMode: () => setMode((prev) => (prev === 'dark' ? 'light' : 'dark')),
  };

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used inside ThemeModeProvider');
  }

  return context;
}
